import gql from 'graphql-tag'
import PropTypes from 'prop-types'
import {useQuery} from '@apollo/client'
import * as d3 from 'd3'
import 'd3-format'
import {get} from 'lodash'
import {renderPersonalInfo} from './renderPersonalInfo'
import {Divider, Grid, Stack, Typography} from '@mui/material'
import CircularProgress from '@mui/material/CircularProgress'
import {useConfig} from '../../../../../store/hooks/config'
import TooltipDataRefetchOnError from '../../../../common/TooltipDataRefetchOnError'
import {usePointOfInterest} from '../../../../../store/hooks/pointOfInterest'
import {useSelectedIndividualHealthFilters} from '../PopulationDensityHelper'
import HealthAndSafetyOutlinedIcon from '@mui/icons-material/HealthAndSafetyOutlined'

import {
  PersonaBox,
  StyledBoxTooltip,
  StyledBody1,
  StyledTooltipData,
  StyledLabelSmallPersona,
  StyledBenefitsEligibilityIconContainer,
  StyledDiseaseContainer,
  StyledPersonaContainer,
  StyledPOIContainer,
  StyledPOIHeaderStack,
  StyledPersonaDescriptionText,
  StyledPersonaAvatar,
  StyledPersonaHeaderStack,
  StyledAccordion,
  StyledAccordionSummary,
  StyledAccordionDetails,
  StyledPOIDetailsGrid,
  StyledPOIDistanceGrid,
  StyledPOIDistanceText,
  StyledDividerWithMargin,
  StyledPaperTooltipWithPadding,
  StyledHouseholdSubtitle,
  StyledDiseaseGridWithMargin,
  StyledOutreachStack,
  StyledOutreachDivider,
  StyledIcon
} from './Styles'
import {getOutreachHouseholdTooltipStats, getPOIImageUrl, getPersonaCustomName} from '../../../helper'
import {useOutreachFilters} from '../../../../../store/hooks/useOutreachFilters'
import {usePersona} from '../../../../../store/hooks/persona'
import {useTheme} from '@mui/material/styles'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import MapsHomeWorkOutlinedIcon from '@mui/icons-material/MapsHomeWorkOutlined'
import ShareLocationIcon from '@mui/icons-material/ShareLocation'
import {classesIP} from '../../../../PersonaSidebar/IndividualPersona/selectedPersonaStyles'
import {Legend} from '../Legends/POILegendStyle'

const getNearPOI = (pointOfInterests, selectedPointOfInterest) => {
  if (!selectedPointOfInterest?.length) {
    return gql`
   query placesOfInterestNearby($location: LatLngPoint, $num: Int, $filters: PlacesOfInterestFilter){
    placesOfInterestNearby(location: $location, num: $num, filters: $filters){
      ${pointOfInterests?.[0].value} {
        distanceInMeters,
        name,
        city,
        state
      }
    }
  }
`
  }

  return gql`
  query placesOfInterestNearby($location: LatLngPoint, $num: Int, $filters: PlacesOfInterestFilter){
    placesOfInterestNearby(location: $location, num: $num, filters: $filters){
      ${selectedPointOfInterest?.map((poi) => {
        return `${poi} {
        distanceInMeters,
        name,
        city,
        state
      }
      `
      })}
    }
  }
`
}

function getMiles(meters) {
  return d3.format('.3~r')(meters * 0.000621371192)
}
const getPersonaHouseHoldDotColor = (theme, persona) => {
  return theme.palette.personaColor?.[persona]?.[0]
}

/**
 * Render outreach household tooltip data using new filter management system
 * @param {string} activeOutreachType - 'benefits' | 'workforce' | null
 * @param {Array} selectedOptionsWithConfig - Array of selected options with config
 * @param {Object} data - Household data
 * @param {string} iconColor - Icon color for styling
 */
const getOutreachHouseholdTooltipData = (activeOutreachType, selectedOptionsWithConfig, data, iconColor) => {
  const tooltipInfo = getOutreachHouseholdTooltipStats(activeOutreachType, selectedOptionsWithConfig, data)
  return tooltipInfo && Object.keys(tooltipInfo).length > 0 ? (
    <>
      <StyledOutreachDivider />
      <StyledOutreachStack direction='row' spacing={1} alignItems='center'>
        <Grid container spacing={2}>
          <Grid xs={1}>
            <StyledBenefitsEligibilityIconContainer colorIcon={iconColor}>
              <StyledIcon>
                <CheckCircleOutlineOutlinedIcon />
              </StyledIcon>
            </StyledBenefitsEligibilityIconContainer>
          </Grid>
          <Grid xs={6}>
            <StyledBody1>Eligible for</StyledBody1>
            <Typography>
              <StyledTooltipData component='span'>{tooltipInfo.eligibleValue}</StyledTooltipData>
            </Typography>
          </Grid>
          <Grid xs={5}>
            <StyledBody1>Enrolled in</StyledBody1>
            <Typography>
              <StyledTooltipData component='span'>{tooltipInfo.enrolledValue}</StyledTooltipData>
            </Typography>
          </Grid>
        </Grid>
      </StyledOutreachStack>
    </>
  ) : null
}

const HouseholdTooltip = ({data, loadingUniqueHouseholds}) => {
  const theme = useTheme()
  const {selectedPOI, existingPOI, pointOfInterests} = usePointOfInterest()
  const healthFiltersToDisplay = useSelectedIndividualHealthFilters() // New filter management system

  // New filter management system for outreach
  const {isOutreachActive, activeOutreachType, selectedOptionsWithConfig} = useOutreachFilters()
  const {personas, personasToggle} = usePersona()
  const {personasConfig} = useConfig()

  // Checking the benefit eligibility data is selected or not . If it is selected checking whether it is enrolled or not.
  const CheckprocessEnrolled = (data) => {
    if (!data) {
      return {text: 'Not Enrolled', color: theme.palette.hpAccentVioletPink}
    }
    const KeyArray = Object.keys(data)
      .filter((key) => key.includes('Enrolled'))
      .map((key) => data[key])

    const allOne = KeyArray.every((value) => value === 1)
    const allZero = KeyArray.every((value) => value === 0)

    if (allZero) {
      return {text: 'Not Enrolled', color: theme.palette.hpAccentVioletPink}
    } else if (allOne) {
      return {text: 'Enrolled', color: theme.palette.heatmapColor.greenDark}
    } else {
      return {text: 'PartiallyEnrolled', color: theme.palette.heatmapColor.greenDark}
    }
  }
  const CheckEnrolledData = CheckprocessEnrolled(data)
  const {address, city, state, numberOfPeople, squareFeet, latitude, longitude, zip, age, householdType, ...rest} = data

  const {POIConfig: poiConfig} = useConfig() || {}

  const miscPiiValue =
    process.env.NODE_ENV === 'production'
      ? globalThis.__env.REACT_APP_PII === 'true'
      : process.env.REACT_APP_PII === 'true'
  const personaProperties = [
    {
      prop: 'Assoicated Persona',
      value: get(rest, `persona`, '')
    },
    {
      prop: 'Persona Cohort',
      value: get(rest, `personaCohort`, '')
    }
  ]

  const {
    data: nearestPOI,
    loading,
    error
  } = useQuery(getNearPOI(pointOfInterests, selectedPOI), {
    variables: {
      location: {latitude, longitude},
      num: 3,
      filters: {}
    },
    skip: !selectedPOI || selectedPOI?.length === 0,
    partialRefetch: true,
    context: {
      debounceKey: 'nearestPOI'
    }
  })

  const PersonalInfo = () => renderPersonalInfo({address, city, state}, numberOfPeople, squareFeet, zip, age)
  const getCustomPoiName = (poi) => {
    return existingPOI?.find((existingPoi) => existingPoi.value === poi)?.customLabel || poiConfig[poi].label
  }

  // Calculate the icon color once for all StyledBenefitsEligibilityIconContainer based on all conditions
  const getIconColor = () => {
    if (isOutreachActive) {
      // When outreach simulation filter is active, use enrollment status colors
      switch (CheckEnrolledData.text) {
        case 'Enrolled':
          return theme.palette.heatmapColor.greenDark
        case 'Not Enrolled':
          return theme.palette.hpAccentVioletPink
        case 'PartiallyEnrolled':
          return theme.palette.heatmapColor.greenDark // or a different color for partial enrollment
        default:
          return theme.palette.hpAccentVioletPink // default fallback
      }
    }
    return personasToggle
      ? getPersonaHouseHoldDotColor(theme, personasConfig?.personas?.[get(rest, `persona`, '')])
      : theme.palette.hpAccentPink
  }
  const iconColor = getIconColor()
  return (
    <StyledBoxTooltip tooltipWidth={380}>
      {/* PERSONAL AND DWELLING INFORMATION */}
      <StyledPaperTooltipWithPadding>
        <Stack direction={'column'} spacing={1}>
          <Stack direction={'row'} alignItems='center' spacing={1}>
            <StyledBenefitsEligibilityIconContainer colorIcon={iconColor}>
              <StyledIcon>
                <MapsHomeWorkOutlinedIcon data-testid='unique-households-circle' />
              </StyledIcon>
            </StyledBenefitsEligibilityIconContainer>
            <StyledHouseholdSubtitle
              color={!personasToggle && (isOutreachActive ? CheckEnrolledData.color : theme.palette.hpAccentDarkerPink)}
            >
              {householdType}
            </StyledHouseholdSubtitle>
            {/* <StyledBody2>-{getCustomNameForDwellingType()}</StyledBody2> */}
          </Stack>
          {miscPiiValue && <PersonalInfo />}
        </Stack>

        {/* SELECTED HEALTH CONDITIONS */}
        {loadingUniqueHouseholds && !loading && <CircularProgress size={20} />}

        {!loadingUniqueHouseholds && healthFiltersToDisplay.length > 0 && (
          <>
            <Divider />

            <StyledDiseaseContainer direction={'row'} spacing={1}>
              <Grid container spacing={2}>
                {healthFiltersToDisplay?.map((filter, index) => {
                  // Map to generic riskPercentile0, riskPercentile1, etc. from the API response
                  const riskValue = get(rest, `riskPercentile${index}`, '')
                  return (
                    <>
                      <StyledDiseaseGridWithMargin xs={1} key={filter.key}>
                        {index === 0 && (
                          <StyledBenefitsEligibilityIconContainer colorIcon={iconColor}>
                            <StyledIcon>
                              <HealthAndSafetyOutlinedIcon />
                            </StyledIcon>
                          </StyledBenefitsEligibilityIconContainer>
                        )}
                      </StyledDiseaseGridWithMargin>
                      <StyledDiseaseGridWithMargin xs={5} key={`${filter.key}-data`}>
                        <StyledBody1>{filter.name}</StyledBody1>
                        <Typography>
                          <StyledTooltipData component='span'>{riskValue ? `${riskValue} %` : ''}</StyledTooltipData>
                        </Typography>
                      </StyledDiseaseGridWithMargin>
                    </>
                  )
                })}
              </Grid>
            </StyledDiseaseContainer>
          </>
        )}

        {/* PERSONA INFORMATION */}
        {personaProperties[0].value && personasToggle && personas?.length > 0 && (
          <>
            <StyledDividerWithMargin />
            <StyledPersonaContainer spacing={2}>
              <Stack direction='row'>
                <Grid xs={1} key={personaProperties[0].value}>
                  <StyledBenefitsEligibilityIconContainer colorIcon={iconColor}>
                    <StyledIcon>
                      <AccountBoxOutlinedIcon />
                    </StyledIcon>
                  </StyledBenefitsEligibilityIconContainer>
                </Grid>

                <Grid xs={11} key={personaProperties[1].value}>
                  <StyledPersonaDescriptionText>Associated Persona:</StyledPersonaDescriptionText>

                  <PersonaBox>
                    <Stack direction='row' gap={1} alignItems='center'>
                      <StyledPersonaAvatar
                        alt={personaProperties[0]?.prop}
                        src={require(`../../../../../icons/Personas/${personaProperties[0]?.value}.jpg`)}
                      />
                      <StyledPersonaHeaderStack
                        className={classesIP.personaHeaderStackMargin}
                        direction='column'
                        tabIndex={0}
                      >
                        <StyledTooltipData>
                          {getPersonaCustomName(personaProperties[0].value, personasConfig)}
                        </StyledTooltipData>
                        <StyledLabelSmallPersona>({personaProperties[1].value})</StyledLabelSmallPersona>
                      </StyledPersonaHeaderStack>
                    </Stack>
                  </PersonaBox>
                </Grid>
              </Stack>
            </StyledPersonaContainer>
          </>
        )}

        {getOutreachHouseholdTooltipData(activeOutreachType, selectedOptionsWithConfig, data, iconColor)}

        {loading && <CircularProgress size={20} />}
        {!loading && error && <TooltipDataRefetchOnError />}
        {!loading &&
          !error &&
          nearestPOI?.placesOfInterestNearby &&
          Object.keys(nearestPOI.placesOfInterestNearby).map((poi, index) => {
            return (
              <StyledPOIContainer key={poi}>
                {index === 0 && <Divider />}

                {index === 0 && (
                  <StyledPOIHeaderStack direction='row' alignItems='center' spacing={1}>
                    <StyledBenefitsEligibilityIconContainer colorIcon={iconColor}>
                      <StyledIcon>
                        <ShareLocationIcon />
                      </StyledIcon>
                    </StyledBenefitsEligibilityIconContainer>
                    <StyledTooltipData>POIs</StyledTooltipData>
                  </StyledPOIHeaderStack>
                )}
                <StyledAccordion defaultExpanded={index === 0}>
                  <StyledAccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls='panel1-content'
                    id='panel1-header'
                    alignItems='center'
                  >
                    <Stack direction='row' alignItems='center' spacing={1}>
                      <Legend alt={`${poi.label} icon`} src={getPOIImageUrl(poiConfig[poi].markerProps.iconURL)} />
                      <StyledTooltipData>Nearest {getCustomPoiName(poi)}</StyledTooltipData>
                    </Stack>
                  </StyledAccordionSummary>
                  <Grid spacing={1} key={getCustomPoiName(poi)}>
                    {nearestPOI.placesOfInterestNearby[poi].map((poiInfo) => {
                      return (
                        <StyledAccordionDetails key={poiInfo.name}>
                          <Stack direction='row'>
                            <StyledPOIDetailsGrid xs={9}>
                              <StyledBody1>{poiInfo.name} </StyledBody1>
                            </StyledPOIDetailsGrid>
                            <StyledPOIDistanceGrid xs={3}>
                              <StyledPOIDistanceText>
                                {getMiles(poiInfo.distanceInMeters)} Mi
                                {/* KEEPING BELOW PART AS NON FUNCTIONAL WILL DECIDE IN FUTURE IF NEEDED
                                 {statesToAbbreviation[selectedState] !== poiInfo.state
                                  ? ` - ${poiInfo.city}, ${poiInfo.state}`
                                  : null} */}
                              </StyledPOIDistanceText>
                            </StyledPOIDistanceGrid>
                          </Stack>
                        </StyledAccordionDetails>
                      )
                    })}
                  </Grid>
                </StyledAccordion>
              </StyledPOIContainer>
            )
          })}
      </StyledPaperTooltipWithPadding>
    </StyledBoxTooltip>
  )
}

HouseholdTooltip.propTypes = {
  data: PropTypes.object,
  loadingUniqueHouseholds: PropTypes.bool
}

export default HouseholdTooltip

/**
 * List of functions and their purpose:
 *
 * - getNearPOI: Constructs a GraphQL query to fetch nearby points of interest based on selected POIs.
 * - getMiles: Converts a distance in meters to miles with formatting.
 * - getPersonaHouseHoldDotColor: Retrieves the dot color for a persona household based on the persona configuration.
 * - getOutreachHouseholdTooltipData: Renders outreach simulation data in the tooltip if available.
 * - CheckprocessEnrolled: Determines the enrollment status (Enrolled, Not Enrolled, Partially Enrolled) and its corresponding color.
 * - HouseholdTooltip: Renders a detailed tooltip for a household, including persona, health, and nearby POI information.
 */
